import { useMutation, ApolloError } from '@apollo/client'

import { MUTATION_UPDATE_ZONE } from '@libs/apollo/mutations'

import { Zone, ZoneUpdateInput, MutationUpdateZoneArgs, LocalFile, RemoteFile, ProjectFileType } from '@types'
import { useUpdateProjectFile } from '@hooks/project/update-project-file.hook'
import { useCreateProjectFile } from '@hooks/project/create-project-file.hook'
import { useDeleteProjectFile } from '@hooks/project/delete-project-file.hook'
import { useSetProjectFileToZone } from '@hooks/project/set-project-file-to-zone.hook'
import { useUnsetProjectFileToZone } from '@hooks/project/unset-project-file-to-zone.hook'

interface Data {
  updateZone: Zone
}

export const useUpdateZone = (): {
  updateZone: (input: ZoneUpdateInput, plans: (LocalFile | RemoteFile)[], projectId: string) => Promise<void>
  loading: boolean
  error: ApolloError | undefined
  data?: Data | null
} => {
  const [updateZone, { loading, error, data }] = useMutation<Data, MutationUpdateZoneArgs>(MUTATION_UPDATE_ZONE, {
    notifyOnNetworkStatusChange: true,
  })

  const { createProjectFile } = useCreateProjectFile()
  const { deleteProjectFile } = useDeleteProjectFile()
  const { updateProjectFile } = useUpdateProjectFile()
  const { setProjectFileToZone } = useSetProjectFileToZone()
  const { unsetProjectFileToZone } = useUnsetProjectFileToZone()

  const _updateZone = async (
    input: ZoneUpdateInput,
    plans: (LocalFile | RemoteFile)[],
    projectId: string,
  ): Promise<void> => {
    // Update zone
    const zoneUpdate = await updateZone({ variables: { input } })

    // Zone ID
    const zoneId = zoneUpdate.data?.updateZone.id
    if (!zoneId) {
      return
    }

    const currentPlan = zoneUpdate.data?.updateZone.plans[0]

    const localPlans: LocalFile[] = plans.filter((plan) => Object.hasOwn(plan, 'data')) as LocalFile[]
    const createdFile = localPlans[0]

    // CREATE PROJECT_FILE WITH CREATED FILE
    if (!currentPlan && createdFile) {
      const projectFile = await createProjectFile(
        {
          projectId: projectId,
          type: ProjectFileType.TypePlan,
          name: createdFile.name,
        },
        createdFile,
      )

      if (projectFile) {
        // LINK PROJECT FILE
        await setProjectFileToZone({ projectFileId: projectFile.id, zoneId })
      }
    }
    // UPDATE PROJECT_FILE WITH CREATED FILE
    else if (currentPlan && createdFile) {
      await updateProjectFile(
        {
          projectFileId: currentPlan.id,
          name: createdFile.name,
          description: currentPlan.description,
        },
        createdFile,
      )
    }

    const remoteDeletePlans: RemoteFile[] = plans.filter(
      (plan) => Object.hasOwn(plan, 'deleted') && (plan as RemoteFile).deleted == true,
    ) as RemoteFile[]
    const deletedFile = remoteDeletePlans[0]

    // DELETE PROJECT_FILE IF THERE NO CREATED FILE
    if (currentPlan && !createdFile && deletedFile) {
      // UNLINK PROJECT FILE
      await unsetProjectFileToZone({ projectFileId: currentPlan.id }, zoneId)

      // DELETE PROJECT FILE
      await deleteProjectFile({ projectFileId: currentPlan.id })
    }
  }

  return { updateZone: _updateZone, loading, error, data }
}
